// Helpers for template settings schemas (feature 012). Mirrors the API rules in
// api/internal/templateschema so the form can show problems before saving.
import type { SettingsField, SettingsSchema } from '../../types';

export const isStoredSeparately = (field: SettingsField) =>
  field.type === 'secret' || field.type === 'file';

const isEmpty = (value: unknown) =>
  value === undefined || value === null || (typeof value === 'string' && value.trim() === '');

export const effectiveValue = (schema: SettingsSchema, features: Record<string, unknown>, key: string) => {
  const value = features[key];
  if (!isEmpty(value)) return value;
  return schema.fields.find((f) => f.key === key)?.default;
};

export const isRequired = (schema: SettingsSchema, field: SettingsField, features: Record<string, unknown>) => {
  if (field.required) return true;
  if (!field.required_if) return false;
  const value = effectiveValue(schema, features, field.required_if.field);
  return field.required_if.in.some((v) => String(v) === String(value));
};

const DOMAIN_LABEL = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

export const isDomain = (value: string) => {
  if (!value || value.length > 253) return false;
  const labels = value.split('.');
  if (labels.length < 2) return false;
  return labels.every((l) => DOMAIN_LABEL.test(l)) && /^([a-z]{2,63}|xn--[a-z0-9-]{1,59})$/.test(labels[labels.length - 1]);
};

/** Host from a pasted value: AppMetrica shows applinks:<host>, browsers https://<host>/. */
export const normalizeDomain = (value: string) =>
  value.trim().toLowerCase().replace(/^(applinks:|https?:\/\/)/, '').replace(/\/.*$/, '');

/** Client-side check of non-secret fields; returns field key → message. */
export const validateSettings = (schema: SettingsSchema, features: Record<string, unknown>) => {
  const errors: Record<string, string> = {};
  for (const field of schema.fields) {
    if (isStoredSeparately(field)) continue;
    const value = features[field.key];
    if (isEmpty(value)) {
      if (isRequired(schema, field, features) && isEmpty(field.default)) {
        errors[field.key] = 'Обязательное поле';
      }
      continue;
    }
    if (field.type === 'domain' && !isDomain(String(value))) {
      errors[field.key] = 'Домен без протокола и пути, например shop.example.com';
    } else if (field.pattern && !new RegExp(field.pattern).test(String(value))) {
      errors[field.key] = `Неверный формат. ${field.help ?? ''}`.trim();
    }
  }
  return errors;
};

/** Value derived from another setting (host part of a URL for domain fields). */
export const prefillValue = (field: SettingsField, features: Record<string, unknown>): string | undefined => {
  if (!field.prefill_from) return undefined;
  const raw = features[field.prefill_from];
  if (typeof raw !== 'string' || raw.trim() === '') return undefined;
  if (field.type !== 'domain') return raw.trim();
  try {
    const url = new URL(raw.includes('://') ? raw.trim() : `https://${raw.trim()}`);
    return url.hostname.toLowerCase() || undefined;
  } catch {
    return undefined;
  }
};

/** Applies defaults and prefills for empty fields of the schema. */
export const withDefaults = (schema: SettingsSchema, features: Record<string, unknown>) => {
  const result = { ...features };
  for (const field of schema.fields) {
    if (isStoredSeparately(field) || !isEmpty(result[field.key])) continue;
    const prefilled = prefillValue(field, result);
    if (prefilled !== undefined) {
      result[field.key] = prefilled;
    } else if (field.default !== undefined) {
      result[field.key] = field.default;
    }
  }
  return result;
};

/** Groups fields preserving schema order. */
export const groupFields = (fields: SettingsField[]) => {
  const groups: Array<{ name: string; fields: SettingsField[] }> = [];
  for (const field of fields) {
    const name = field.group || '';
    let group = groups.find((g) => g.name === name);
    if (!group) {
      group = { name, fields: [] };
      groups.push(group);
    }
    group.fields.push(field);
  }
  return groups;
};
